<script>
import { mapGetters } from 'vuex';

import GetInTouchForm from '@components/forms/get-in-touch.vue';
import StayInTouchForm from '@components/forms/stay-in-touch.vue';
import UsersList from '@components/contact-us-page/users-list.vue';

import Translatable from '@src/mixins/translatable.js';
import SanitizerMixin from '@src/mixins/sanitizer-mixin.js';
import ImageMixin from '@src/mixins/imageMixin.js';
import Resizable from '@src/mixins/resizable.js';
import AuthorLabel from '@src/mixins/author.js';
import { sharedBlocksMixin } from '@src/mixins/sharedBlocksMixin.js';
import SeoMixin from '@src/mixins/seo.js';

import SharedBlockSlot from '@components/sharedBlocks/SharedBlockSlot.vue';
import Layout from '@layouts/main.vue';

export default {
  /*
  page: {
    title: 'Contact Us',
    meta: [{ name: 'description', content: 'Contact Us' }],
  },
  */
  components: {
    Layout,
    GetInTouchForm,
    StayInTouchForm,
    UsersList,
    SharedBlockSlot,
  },
  mixins: [
    Translatable,
    SanitizerMixin,
    ImageMixin,
    Resizable,
    AuthorLabel,
    sharedBlocksMixin,
    SeoMixin,
  ],
  provide() {
    const provided = {};
    Object.defineProperty(provided, 'sortedSharedBlocks', {
      enumerable: true,
      get: () => this.sortedSharedBlocks,
    });
    return {
      sharedBlocksProvider: provided,
    };
  },
  computed: {
    ...mapGetters('contactUs', ['pageData']),
    ...mapGetters('translations', ['currentLanguage']),

    pageImg() {
      if (this.pageData.mainImage) {
        return this.desktopScreen ? this.pageData.mainImage.web : this.pageData.mainImage.app;
      }
      return null;
    },

    seoEntity() {
      return {
        ...this.pageData,
        isStatic: true,
      };
    },
  },

  watch: {
    pageData: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal.slugs) {
          this.$store.commit('translations/SET_ALTERNATE_SLUGS', newVal.slugs);
        }
      },
    },
  },
  methods: {
    getSeoText(obj) {
      if (!obj) return '';
      return obj[this.currentLanguage] || obj.en || '';
    },
  },
};
</script>

<template>
  <Layout>
    <section
      v-if="pageImg"
      class="header justify-center"
      :style="getBackground(pageImg.url)"
      :title="getSeoText(pageImg.title)"
      role="img"
      :aria-label="getSeoText(pageImg.alt)"
    >
      <div class="header-block">
        <h1 class="header-title" v-html="getTranslatedContent('title', pageData.content)"></h1>
        <h3
          class="header-subtitle"
          v-html="getTranslatedContent('subtitle', pageData.content)"
        ></h3>
      </div>
    </section>
    <section class="description-banner justify-center">
      <p class="author container" v-html="authorLabel(pageData)"></p>
      <div class="description">
        <div class="text-container">
          <div v-html="bannerPageText(pageData.content)"></div>
        </div>
        <router-link :to="$l('faq')" class="button-container">
          <button class="button-send">
            {{ getTranslatedContent('CONTACT_US_check_faq_btn') }}
          </button>
        </router-link>
      </div>
    </section>
    <section class="justify-center">
      <div class="container">
        <GetInTouchForm />
        <StayInTouchForm />
        <UsersList />
      </div>
    </section>
    <SharedBlockSlot :slot-index="1" />
    <SharedBlockSlot :slot-index="2" />
    <SharedBlockSlot :slot-index="3" />
    <SharedBlockSlot :slot-index="4" />
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
@import '@styles/author_text.scss';

.header {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
  height: 550px;
  width: 100%;
}

.header-block {
  width: 840px;
  height: 135px;
  // background: rgba(37, 44, 62, 0.36);
  padding-top: 9px;
  box-sizing: border-box;
  margin-top: 217px;
  margin-right: 300px;

  .header-title {
    font-family: Open Sans Bold, sans-serif;
    font-weight: bold;
    font-size: 48px;
    line-height: 62px;
    text-transform: uppercase;
    margin-left: 22px;
    color: $white;
  }

  .header-subtitle {
    font-family: Open Sans Semibold, sans-serif;
    font-weight: 600;
    font-size: 22px;
    line-height: 29px;
    margin-left: 25px;
    color: $white;
  }
}

::v-deep .description-banner {
  margin: -99px 0 40px;
  position: relative;
  & .description {
    width: 1140px;
    min-height: 100px;
    height: auto;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    border-radius: 20px;
    box-sizing: border-box;
    background: $white;
    box-shadow: 0 8px 12px rgba(76, 72, 124, 0.08);
    padding: 25px 40px 25px 68px;

    .text-container {
      width: 735px;
      user-select: none;

      & p:empty {
        height: 15px;
        margin: 0;
      }

      & p:not(:last-child):not(:empty) {
        margin-bottom: 15px;
      }

      & p > br {
        display: none;
      }
      & p:has(br:last-child)::after {
        content: '';
        display: block;
        height: 15px;
      }

      & em {
        font-style: italic;
      }

      & a {
        color: $gray;
        text-decoration: none;

        &:hover {
          color: $orange;
        }
      }

      & ul {
        margin-left: 20px;
        list-style: initial;
      }

      & strong {
        font-family: Open Sans Bold, sans-serif;
        font-style: normal;
        font-weight: bold;
      }

      & .ql-align-center {
        text-align: center;
      }
    }

    p {
      font-family: Open Sans, sans-serif;
      font-weight: normal;
      font-style: normal;
      font-size: 16px;
      line-height: 21px;
      color: $dark-blue;
    }

    .bold {
      font-family: Open Sans Bold, sans-serif;
      font-weight: bold;
    }
  }
}

.button-container {
  min-width: 221px;
  align-self: center;
}

.button-send {
  width: 100%;
  height: 48px;
  border: 1px solid $dark-blue;
  border-radius: 5px;
  background: $dark-blue;
  color: $white;
  font-family: Open Sans Bold, sans-serif;
  font-weight: bold;
  font-style: normal;
  text-transform: uppercase;
  font-size: 15px;
  line-height: 130%;
  padding: 15px 30px;

  &:hover {
    border: 1px solid $hover-blue;
    background: $hover-blue;
  }
}

@media only screen and (max-width: $screen-width-xs) {
  .container {
    width: 100%;
  }

  .header {
    height: 244px;
  }

  .header-block {
    width: 100%;
    margin-top: 70px;
    margin-right: 0;
    height: 125px;

    .header-title {
      font-family: Open Sans Bold, sans-serif;
      font-weight: bold;
      font-size: 26px;
      line-height: 34px;
      text-transform: uppercase;
      margin: 20px 8px 10px;
      color: $white;
    }

    .header-subtitle {
      font-family: Open Sans Semibold, sans-serif;
      font-weight: 600;
      font-size: 16px;
      line-height: 21px;
      color: $white;
      margin: 0 8px;
    }
  }

  .description-banner {
    margin: -20px 0 40px;

    & .description {
      flex-direction: column;
      width: 100%;
      min-height: 240px;
      padding: 20px 8px;

      .text-container {
        width: 100%;
      }

      p {
        font-size: 14px;
        line-height: 23px;
      }
      .bold {
        line-height: 18px;
      }
    }
  }

  .button-container {
    width: 100%;
  }
}
</style>
